import { isEmpty, isEqual, isNil } from 'lodash';
import { CSSProperties, useCallback, useContext, useMemo, useState } from 'react';
import { Button, ClearButton } from '@shared/components/Button';
import { View } from '@shared/components/View';
import { MBAutosizeTextInput } from '@shared/components/textInput';
import { TodoField } from '@shared/data/entity/fields/TodoField';
import { useComponentFocus } from '@shared/scenes/cards/form/components/ComponentFocus';
import { ColorThemeContext, bounceIn } from '@shared/styles';
import { API } from '@shared/types/api';
import { TodoItem } from '@shared/types/fieldValues';
import { i18n } from '@shared/util/i18n';
import { dismissKeyboard } from '@shared/util/keyboard';
import { scrollActiveElementIntoView } from '@shared/util/scrollIntoView';
import { uuid } from '@shared/util/uuid';
import { UpdateField } from '../../types';
import { EditFieldContainer } from './EditFieldContainer';
import { useFieldDraft } from './useFieldDraft';
import styles from './TodoEditField.module.css';

interface TodoEditFieldProps {
  component: API.TodoComponent;
  field: TodoField | null;
  updateField: UpdateField;
  error?: string;
  allowClear?: boolean;
  style?: CSSProperties;
}

type PartialTodoItem = Partial<TodoItem> & { id: string };

export function TodoEditField({
  field,
  component,
  updateField,
  error,
  allowClear,
  style,
}: TodoEditFieldProps) {
  // The field is the backing state. `draft` only holds summaries the user is still typing,
  // whose trailing whitespace trimming would eat, and yields whenever the field changes from
  // outside this component.
  const fieldTodos = useMemo(() => field?.todos ?? [], [field?.todos]);
  const [draft, commitDraft] = useFieldDraft<TodoItem[], TodoItem[]>(fieldTodos, isEqual);
  const todos = draft ?? fieldTodos;
  const [newTodo, setNewTodo] = useState<TodoItem>(createNewTodoItem);
  const existingTodos = useMemo(
    () => todos.filter(todo => todo.id !== newTodo.id),
    [todos, newTodo.id],
  );

  const { setComponentFocused, setComponentBlurred } = useComponentFocus();

  const writeTodos = useCallback(
    (updatedTodos: TodoItem[]) => {
      const trimmedTodos = updatedTodos.map(trimTodoSummary);
      commitDraft(updatedTodos, trimmedTodos);
      updateField(component.id, new TodoField(trimmedTodos));
    },
    [updateField, component.id, commitDraft],
  );

  const handleFocus = useCallback(() => {
    scrollActiveElementIntoView();
    setComponentFocused();
  }, [setComponentFocused]);

  const handleBlur = useCallback(() => {
    setComponentBlurred();
  }, [setComponentBlurred]);

  const updateTodo = useCallback(
    (updatedItem: PartialTodoItem) => {
      writeTodos(
        todos.map(todo => (todo.id === updatedItem.id ? { ...todo, ...updatedItem } : todo)),
      );
    },
    [todos, writeTodos],
  );

  const removeTodo = useCallback(
    (id: string) => {
      writeTodos(todos.filter(todo => todo.id !== id));
    },
    [todos, writeTodos],
  );

  // Edits the trailing "add another" row, which lives in local state and earns its place in
  // the saved list by having text: typing adds it, later keystrokes update it in place, and
  // clearing the text takes it back out. An item is saved as it is typed, and an abandoned one
  // leaves nothing behind.
  const updateNewTodo = useCallback(
    (updatedItem: PartialTodoItem) => {
      const updatedNewTodo = { ...newTodo, ...updatedItem };
      setNewTodo(updatedNewTodo);

      const isEmptySummary = isEmpty(updatedNewTodo.summary.trim());
      const isIncluded = todos.some(todo => todo.id === updatedNewTodo.id);
      if (isIncluded && isEmptySummary) {
        writeTodos(todos.filter(todo => todo.id !== updatedNewTodo.id));
      } else if (isIncluded) {
        writeTodos(todos.map(todo => (todo.id === updatedNewTodo.id ? updatedNewTodo : todo)));
      } else if (!isEmptySummary) {
        writeTodos([...todos, updatedNewTodo]);
      }
    },
    [newTodo, todos, writeTodos],
  );

  const clearNewTodo = useCallback(() => {
    updateNewTodo({ id: newTodo.id, summary: '' });
  }, [updateNewTodo, newTodo.id]);

  const resetNewTodo = useCallback(() => {
    setNewTodo(createNewTodoItem());
  }, []);

  const onDone = useCallback(() => {
    const isNewTodoEmpty = isEmpty(newTodo.summary.trim());
    if (isNewTodoEmpty) {
      dismissKeyboard();
    } else {
      resetNewTodo();
    }
  }, [newTodo.summary, resetNewTodo]);

  const onPaste = useCallback(
    (pastedText: string) => {
      const pastedTodos = getPastedTodos(pastedText);
      if (pastedTodos.length < 2) {
        return true;
      }
      writeTodos([...todos, ...pastedTodos]);
      resetNewTodo();
      requestAnimationFrame(scrollActiveElementIntoView);
      return false;
    },
    [resetNewTodo, todos, writeTodos],
  );

  const label = getComponentLabel(component, field?.todos ?? []);
  const placeholder = component.placeholder ?? i18n('card.blocks.todoInput.addPlaceholder');
  const hasItems = !isEmpty(field?.todos);
  const isReadOnly = !!component.readonly;
  if (isReadOnly && !hasItems) {
    return null;
  }

  const printContent =
    existingTodos.map(t => `[${isCompleted(t) ? 'x' : ' '}] ${t.summary}`).join('\n') || undefined;

  return (
    <EditFieldContainer
      component={component}
      label={label}
      showLabel={true}
      showIcon={false}
      error={error}
      style={style}
      printContent={printContent}
    >
      <View marginTop={0}>
        {existingTodos.map(todoItem => (
          <TodoItemField
            key={todoItem.id}
            todoItem={todoItem}
            updateTodo={updateTodo}
            removeTodo={removeTodo}
            placeholder={placeholder}
            isReadOnly={isReadOnly}
            onDone={dismissKeyboard}
            onFocus={handleFocus}
            onBlur={handleBlur}
            error={error}
            allowClear={allowClear}
          />
        ))}
        {!isReadOnly && (
          <TodoItemField
            isNew={true}
            todoItem={newTodo}
            updateTodo={updateNewTodo}
            removeTodo={clearNewTodo}
            placeholder={placeholder}
            isReadOnly={isReadOnly}
            onPaste={onPaste}
            onDone={onDone}
            onFocus={handleFocus}
            onBlur={handleBlur}
            error={error}
            allowClear={allowClear}
          />
        )}
      </View>
    </EditFieldContainer>
  );
}

interface TodoItemFieldProps {
  todoItem: TodoItem;
  placeholder: string;
  isNew?: boolean;
  isReadOnly: boolean;
  canUpdateStatus?: boolean;
  updateTodo: (partialTodo: PartialTodoItem) => void;
  removeTodo: (id: string) => void;
  onPaste?: (pastedText: string) => void;
  onDone: VoidFunction;
  onFocus: VoidFunction;
  onBlur: VoidFunction;
  error?: string;
  allowClear?: boolean;
}

function TodoItemField({
  todoItem,
  placeholder,
  isNew = false,
  isReadOnly = false,
  updateTodo,
  removeTodo,
  onPaste,
  onDone,
  onFocus,
  onBlur,
  error,
  allowClear = true,
}: TodoItemFieldProps) {
  const colors = useContext(ColorThemeContext);
  const { id, summary } = todoItem;

  const setSummary = useCallback(
    (text: string) => updateTodo({ id, summary: text }),
    [id, updateTodo],
  );

  const markComplete = useCallback(() => {
    updateTodo({ id, status: 'COMPLETED' });
  }, [id, updateTodo]);

  const markIncomplete = useCallback(() => {
    updateTodo({ id, status: 'NEEDS-ACTION' });
  }, [id, updateTodo]);

  const onClear = useCallback(() => {
    removeTodo(id);
  }, [id, removeTodo]);

  const isComplete = !isNew && isCompleted(todoItem);
  const hasValue = !isEmpty(summary.trim());
  // An existing item can always be removed, including after its text has been
  // cleared — otherwise an emptied row is stranded in the list with no way to
  // get rid of it. The trailing "add another" row has nothing to remove until
  // it has a value.
  const showClearButton = !isReadOnly && allowClear && (!isNew || hasValue);

  return (
    <View row>
      <View center className={styles.checkbox}>
        {isNew && (
          <Button
            variant="ghost"
            size="medium"
            iconOnly
            icon="plus-circle"
            iconGridSize={4.5}
            label={placeholder}
            className={[styles.addButton, !isEmpty(error) && styles.error]}
            disabled={!hasValue}
            onClick={onDone}
          />
        )}
        {!isNew && (
          <Button
            variant="ghost"
            size="medium"
            iconOnly
            icon={isComplete ? 'task-filled' : 'unfilled-circle'}
            iconGridSize={4.5}
            label={summary || placeholder}
            className={[styles.checkboxButton, isComplete && [styles.complete, bounceIn]]}
            role="checkbox"
            aria-checked={isComplete}
            disabled={isReadOnly}
            onClick={isComplete ? markIncomplete : markComplete}
          />
        )}
      </View>
      <MBAutosizeTextInput
        value={summary}
        onChangeText={setSummary}
        returnKeyType="done"
        padding="none"
        blurOnSubmit={!isNew}
        placeholder={placeholder}
        color={isComplete ? colors.foregroundInactive : colors.foregroundActive}
        onFocus={onFocus}
        onBlur={onBlur}
        onPaste={onPaste}
        onSubmitEditing={onDone}
        disabled={isReadOnly || isComplete}
        style={{
          flex: 1,
          alignSelf: 'center',
          marginTop: 0,
          marginBottom: 0,
          textDecorationLine: isComplete ? 'line-through' : 'none',
        }}
      />
      {showClearButton && (
        <View padding={1}>
          <ClearButton onClick={onClear} />
        </View>
      )}
    </View>
  );
}

function getComponentLabel(component: API.TodoComponent, todos: TodoItem[]): string | undefined {
  const { label } = component;
  if (isNil(label)) {
    return undefined;
  }

  const total = todos.length;
  if (total === 0) {
    return label;
  }

  const completed = todos.filter(isCompleted).length;
  return `${label} (${completed}/${total})`;
}

function createNewTodoItem(summary = ''): TodoItem {
  return {
    summary,
    status: 'NEEDS-ACTION',
    id: uuid(),
  };
}

function isCompleted(todoItem: TodoItem): boolean {
  return todoItem.status === 'COMPLETED';
}

function trimTodoSummary(todoItem: TodoItem): TodoItem {
  return { ...todoItem, summary: todoItem.summary.trim() };
}

function getPastedTodos(pastedText: string): TodoItem[] {
  const pastedLines = pastedText
    .split('\n')
    .map(line => line.trim())
    .filter(line => !!line);

  return pastedLines.map(createNewTodoItem);
}
